import type { Message, Request } from '@dia/db';
import { t } from '@dia/i18n';

/**
 * Words on a request and a message, as pure functions.
 *
 * A request or a message is stored once in the language it was written in,
 * with a translated shadow beside it (Section 10). The reader sees their own
 * language, and when the shadow is missing they see the original, honestly
 * marked, rather than a blank.
 */

export type Lang = 'es' | 'en';

export function requestText(r: Pick<Request, 'text_original' | 'lang_original' | 'text_es' | 'text_en'>, lang: Lang): string {
  return (lang === 'es' ? r.text_es : r.text_en) ?? r.text_original;
}

export type Shown = { text: string; translated: boolean; original: string; originalLang: Lang };

export function messageText(
  m: Pick<Message, 'text_original' | 'lang_original' | 'text_es' | 'text_en'>,
  lang: Lang,
): Shown {
  if (m.lang_original === lang) {
    return { text: m.text_original, translated: false, original: m.text_original, originalLang: m.lang_original };
  }
  const shadow = lang === 'es' ? m.text_es : m.text_en;
  return {
    text: shadow ?? m.text_original,
    translated: shadow !== null && shadow !== undefined,
    original: m.text_original,
    originalLang: m.lang_original,
  };
}

/**
 * Photos on a request are stored as paths. A photo that was taken as evidence
 * of a cleaning lives in the run-photos bucket, and is written as
 * "run-photos:path" so a reader knows where to fetch it. Everything else is in
 * request-photos.
 */
export function bucketAndPath(entry: string): { bucket: string; path: string } {
  const colon = entry.indexOf(':');
  const slash = entry.indexOf('/');
  if (colon > 0 && (slash === -1 || colon < slash)) {
    return { bucket: entry.slice(0, colon), path: entry.slice(colon + 1) };
  }
  return { bucket: 'request-photos', path: entry };
}

export type RequestTarget = {
  assetEs?: string | null;
  assetEn?: string | null;
  roomEs?: string | null;
  roomEn?: string | null;
};

export type ComposedRequest = {
  text_original: string;
  lang_original: Lang;
  text_es: string | null;
  text_en: string | null;
};

/**
 * The text of a maintenance report. The structured part (what and where) is
 * written in both languages from the dictionary, so it never needs the
 * translate function. Only the free note does: when it has been translated the
 * request carries both languages, and when the phone was offline it carries
 * the note in its own language only and the other side stays empty until a
 * translation exists.
 */
export function composeMaintenanceText(
  target: RequestTarget,
  note: { text: string; lang: Lang; translated: string | null },
): ComposedRequest {
  const line = (lang: Lang): string => {
    const what = lang === 'es' ? target.assetEs : target.assetEn;
    const where = lang === 'es' ? target.roomEs : target.roomEn;
    if (what && where) return t('ops.maintenance.line_broken_at', lang, { what, where });
    if (what) return t('ops.maintenance.line_broken', lang, { what });
    return t('ops.maintenance.line_place', lang, { where: where ?? '' });
  };

  const text = note.text.trim();
  const other: Lang = note.lang === 'es' ? 'en' : 'es';
  const join = (lang: Lang, extra: string) => (extra ? `${line(lang)}. ${extra}` : line(lang));

  const own = join(note.lang, text);
  const shadow = text ? (note.translated ? join(other, note.translated.trim()) : null) : join(other, '');

  return {
    text_original: own,
    lang_original: note.lang,
    text_es: note.lang === 'es' ? own : shadow,
    text_en: note.lang === 'en' ? own : shadow,
  };
}
